import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Briefcase,
  Calendar,
  DollarSign,
  FileText,
  Globe,
  MapPin,
  Plus,
  Save,
  Users,
  X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';

const JobForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: {
      experience: {
        min: '',
        max: '',
        preferred: ''
      },
      education: {
        degree: '',
        fieldOfStudy: '',
        required: false
      },
      skills: [],
      languages: [],
      certifications: [],
      other: []
    },
    responsibilities: [],
    qualifications: [],
    benefits: {
      salary: [],
      health: [],
      time: [],
      lifestyle: [],
      development: [],
      other: []
    },
    company: {
      name: '',
      logo: {
        public_id: '',
        url: ''
      },
      website: '',
      industry: '',
      size: '',
      description: ''
    },
    location: {
      city: '',
      state: '',
      country: 'USA',
      zipCode: '',
      isRemote: false
    },
    workMode: 'On-site',
    employmentType: 'Full-time',
    level: 'Mid Level',
    department: '',
    salary: {
      min: '',
      max: '',
      currency: 'USD',
      period: 'Yearly',
      negotiable: true
    },
    applicationDeadline: '',
    startDate: '',
    featured: false,
    urgent: false,
    status: 'Draft'
  });
  
  const [currentSkill, setCurrentSkill] = useState('');
  const [currentRequirement, setCurrentRequirement] = useState('');
  const [currentResponsibility, setCurrentResponsibility] = useState('');
  const [currentQualification, setCurrentQualification] = useState('');
  const [currentBenefit, setCurrentBenefit] = useState('');
  const [currentLanguage, setCurrentLanguage] = useState('');

  useEffect(() => {
    if (isEditing) {
      fetchJob();
    }
  }, [id]);

  const fetchJob = async () => {
    try {
      setLoading(true);
      console.log('🔥 EMPLOYER - Fetching job for edit:', id);
      
      const response = await api.get(`/jobs/${id}`);
      console.log('📥 EMPLOYER - Job data received:', response.data);
      
      const job = response.data?.data || response.data;
      
      // Safely map job data with proper null checks
      setFormData({
        title: job.title || '',
        description: job.description || '',
        requirements: {
          experience: {
            min: job.requirements?.experience?.min !== undefined && job.requirements?.experience?.min !== null ? String(job.requirements.experience.min) : '',
            max: job.requirements?.experience?.max || '',
            preferred: job.requirements?.experience?.preferred || ''
          },
          education: {
            degree: job.requirements?.education?.degree || '',
            fieldOfStudy: job.requirements?.education?.fieldOfStudy || '',
            required: job.requirements?.education?.required || false
          },
          skills: job.requirements?.skills || [],
          languages: job.requirements?.languages || [],
          certifications: job.requirements?.certifications || [],
          other: job.requirements?.other || []
        },
        responsibilities: job.responsibilities || [],
        qualifications: job.qualifications || [],
        benefits: {
          salary: job.benefits?.salary || [],
          health: job.benefits?.health || [],
          time: job.benefits?.time || [],
          lifestyle: job.benefits?.lifestyle || [],
          development: job.benefits?.development || [],
          other: job.benefits?.other || []
        },
        company: {
          name: job.company?.name || '',
          logo: {
            public_id: job.company?.logo?.public_id || '',
            url: job.company?.logo?.url || ''
          },
          website: job.company?.website || '',
          industry: job.company?.industry || '',
          size: job.company?.size || '',
          description: job.company?.description || ''
        },
        location: {
          city: job.location?.city || '',
          state: job.location?.state || '',
          country: job.location?.country || 'USA',
          zipCode: job.location?.zipCode || '',
          isRemote: job.location?.isRemote || false
        },
        workMode: job.workMode || 'On-site',
        employmentType: job.employmentType || 'Full-time',
        level: job.level || 'Mid Level',
        department: job.department || '',
        salary: {
          min: job.salary?.min || '',
          max: job.salary?.max || '',
          currency: job.salary?.currency || 'USD',
          period: job.salary?.period || 'Yearly',
          negotiable: job.salary?.negotiable !== undefined ? job.salary.negotiable : true
        },
        applicationDeadline: job.applicationDeadline 
          ? new Date(job.applicationDeadline).toISOString().split('T')[0] 
          : '',
        startDate: job.startDate 
          ? new Date(job.startDate).toISOString().split('T')[0] 
          : '',
        featured: job.featured || false,
        urgent: job.urgent || false,
        status: job.status || 'Draft'
      });
      
      console.log('✅ EMPLOYER - Form data set successfully');
    } catch (error) {
      console.error('❌ EMPLOYER - Error fetching job:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error('Failed to load job details');
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
      setFormData(prev => ({
        ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };



  const addToList = (listName, value, setter) => {
    if (!value.trim()) return;
    
    // Handle nested object paths like 'requirements.other'
    const pathParts = listName.split('.');
    
    setFormData(prev => {
      const newData = { ...prev };
      
      // Navigate to the correct nested array
      let current = newData;
      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current[pathParts[i]];
      }
      
      const finalKey = pathParts[pathParts.length - 1];
      const currentArray = current[finalKey] || [];
      
      // Check if value already exists
      if (!currentArray.includes(value.trim())) {
        current[finalKey] = [...currentArray, value.trim()];
      }
      
      return newData;
    });
    
    setter('');
  };

  const removeFromList = (listName, index) => {
    const pathParts = listName.split('.');
    
    setFormData(prev => {
      const newData = { ...prev };
      
      // Navigate to the correct nested array
      let current = newData;
      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current[pathParts[i]];
      }
      
      const finalKey = pathParts[pathParts.length - 1];
      current[finalKey] = current[finalKey].filter((_, i) => i !== index);
      
      return newData;
    });
  };

  const validateForm = () => {
    console.log('🔥 EMPLOYER - Validating form data:', JSON.stringify(formData, null, 2));
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Job title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Job description is required';
    }

    if (!formData.location.city.trim()) {
      newErrors.locationCity = 'City is required';
    }

    if (!formData.location.country.trim()) {
      newErrors.locationCountry = 'Country is required';
    }

    if (!formData.workMode) {
      newErrors.workMode = 'Work mode is required';
    }

    if (formData.requirements.experience.min === '' || formData.requirements.experience.min < 0) {
      newErrors.experienceMin = 'Minimum experience is required and must be 0 or greater';
    }

    if (formData.salary.min && formData.salary.max) {
      if (Number(formData.salary.min) >= Number(formData.salary.max)) {
        newErrors.salaryMax = 'Maximum salary must be greater than minimum salary';
      }
    }

    if (formData.applicationDeadline) {
      const deadline = new Date(formData.applicationDeadline);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (deadline < today) {
        newErrors.applicationDeadline = 'Application deadline cannot be in the past';
      }
    }

    console.log('🔥 EMPLOYER - Validation errors:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e, statusOverride = null) => {
    e.preventDefault();
    
    console.log('🔥 EMPLOYER - JobForm handleSubmit started');
    console.log('🔥 EMPLOYER - Form data before validation:', JSON.stringify(formData, null, 2));
    
    if (!validateForm()) {
      console.log('❌ EMPLOYER - Form validation failed');
      toast.error('Please fix the errors before submitting');
      return;
    }

    setSubmitting(true);

    try {
      // Prepare job data to match backend schema exactly
      const jobData = {
        // Basic Information
        title: formData.title.trim(),
        description: formData.description.trim(),
        
        // Company Information (will be filled from employer profile in backend)
        company: {
          name: formData.company.name || '', // Let backend use employer's company name
          logo: formData.company.logo,
          website: formData.company.website,
          industry: formData.company.industry,
          size: formData.company.size,
          description: formData.company.description
        },
        
        // Location (required structure)
        location: {
          city: formData.location.city || 'City',
          state: formData.location.state,
          country: formData.location.country || 'USA',
          zipCode: formData.location.zipCode,
          isRemote: formData.workMode === 'Remote'
        },
        
        // Job Details (match backend enum values exactly)
        level: formData.level,
        employmentType: formData.employmentType,
        workMode: formData.workMode,
        department: formData.department,
        
        // Requirements (required structure)
        requirements: {
          experience: {
            min: formData.requirements.experience.min === '' ? 0 : Number(formData.requirements.experience.min),
            max: formData.requirements.experience.max ? Number(formData.requirements.experience.max) : undefined,
            preferred: formData.requirements.experience.preferred
          },
          education: formData.requirements.education,
          skills: formData.requirements.skills.map(skill => ({
            name: skill.name || skill,
            level: skill.level || 'Intermediate',
            mandatory: skill.mandatory || false
          })),
          languages: formData.requirements.languages,
          certifications: formData.requirements.certifications,
          other: formData.requirements.other
        },
        
        // Salary
        salary: {
          min: formData.salary.min ? Number(formData.salary.min) : undefined,
          max: formData.salary.max ? Number(formData.salary.max) : undefined,
          currency: formData.salary.currency,
          period: formData.salary.period,
          negotiable: formData.salary.negotiable
        },
        
        // Benefits
        benefits: formData.benefits,
        
        // Dates
        applicationDeadline: formData.applicationDeadline || undefined,
        startDate: formData.startDate || undefined,
        
        // Flags
        featured: formData.featured,
        urgent: formData.urgent,
        status: statusOverride ?? formData.status
      };

      console.log('🔥 EMPLOYER - Prepared job data for API:', JSON.stringify(jobData, null, 2));

      let response;
      if (isEditing) {
        console.log('🔥 EMPLOYER - Updating existing job:', id);
        response = await api.put(`/jobs/${id}`, jobData);
        console.log('✅ EMPLOYER - Job update response:', response.data);
        toast.success('Job updated successfully!');
      } else {
        console.log('🔥 EMPLOYER - Creating new job');
        response = await api.post('/jobs', jobData);
        console.log('✅ EMPLOYER - Job creation response:', response.data);
        toast.success('Job posted successfully!');
      }

      console.log('🔥 EMPLOYER - Navigating to /jobs');
      navigate('/jobs');
    } catch (error) {
      console.error('❌ EMPLOYER - Error saving job:', {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.join(', ') || 
                          'Failed to save job';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAsDraft = (e) => {
    e.preventDefault();
    console.log('🔥 EMPLOYER - Saving as draft');
    handleSubmit(e, 'Draft');
  };

  const handlePublish = (e) => {
    e.preventDefault();
    console.log('🔥 EMPLOYER - Publishing job');
    handleSubmit(e, 'Active');
  };

  if (loading) {
  return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-8"></div>
          <div className="space-y-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded-xl"></div>
            ))}
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-8"
      >
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/jobs')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditing ? 'Edit Job' : 'Post New Job'}
            </h1>
            <p className="text-gray-600">
              {isEditing ? 'Update your job posting' : 'Create a new job posting to attract candidates'}
            </p>
          </div>
        </div>
      </motion.div>
      
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <Briefcase className="w-5 h-5 mr-2" />
            Basic Information
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="lg:col-span-2">
              <label className="form-label">Job Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`input-field ${errors.title ? 'border-red-300' : ''}`}
                placeholder="e.g. Senior Frontend Developer"
              />
              {errors.title && <p className="mt-2 text-sm text-red-600">{errors.title}</p>}
            </div>
            
            <div>
              <label className="form-label">Job Type *</label>
              <select
                name="employmentType"
                value={formData.employmentType}
                onChange={handleChange}
                className="input-field"
              >
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Contract">Contract</option>
                <option value="Freelance">Freelance</option>
                <option value="Internship">Internship</option>
                <option value="Temporary">Temporary</option>
              </select>
            </div>
            
            <div>
              <label className="form-label">Experience Level *</label>
              <select
                name="level"
                value={formData.level}
                onChange={handleChange}
                className="input-field"
              >
                <option value="Entry Level">Entry Level</option>
                <option value="Mid Level">Mid Level</option>
                <option value="Senior Level">Senior Level</option>
                <option value="Executive">Executive</option>
                <option value="Internship">Internship</option>
              </select>
                </div>

            <div>
              <label className="form-label">Department</label>
                <input
                  type="text"
                name="department"
                value={formData.department}
                  onChange={handleChange}
                className="input-field"
                placeholder="e.g. Engineering, Marketing"
                />
              </div>
            </div>
        </motion.div>

        {/* Location Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <MapPin className="w-5 h-5 mr-2" />
            Location & Work Type
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="form-label">City *</label>
              <input
                type="text"
                name="location.city"
                value={formData.location?.city || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  location: { ...prev.location, city: e.target.value }
                }))}
                className={`input-field ${errors.locationCity ? 'border-red-300' : ''}`}
                placeholder="e.g. New York"
              />
              {errors.locationCity && <p className="mt-2 text-sm text-red-600">{errors.locationCity}</p>}
            </div>
            
            <div>
              <label className="form-label">State/Province</label>
              <input
                type="text"
                name="location.state"
                value={formData.location?.state || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  location: { ...prev.location, state: e.target.value }
                }))}
                className="input-field"
                placeholder="e.g. NY"
              />
            </div>
            
            <div>
              <label className="form-label">Country *</label>
                <input
                type="text"
                name="location.country"
                value={formData.location?.country || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  location: { ...prev.location, country: e.target.value }
                }))}
                className={`input-field ${errors.locationCountry ? 'border-red-300' : ''}`}
                placeholder="e.g. United States"
              />
              {errors.locationCountry && <p className="mt-2 text-sm text-red-600">{errors.locationCountry}</p>}
            </div>
            
            <div>
              <label className="form-label">Work Mode *</label>
              <select
                name="workMode"
                value={formData.workMode}
                onChange={handleChange}
                className="input-field"
              >
                <option value="On-site">On-site</option>
                <option value="Remote">Remote</option>
                <option value="Hybrid">Hybrid</option>
              </select>
                </div>

            <div>
              <label className="form-label">Minimum Experience (Years) *</label>
                <input
                type="number"
                name="requirements.experience.min"
                value={formData.requirements?.experience?.min === '' ? '' : formData.requirements.experience.min}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  requirements: {
                    ...prev.requirements,
                    experience: { ...prev.requirements?.experience, min: e.target.value }
                  }
                }))}
                className={`input-field ${errors.experienceMin ? 'border-red-300' : ''}`}
                placeholder="0"
                min="0"
              />
              {errors.experienceMin && <p className="mt-2 text-sm text-red-600">{errors.experienceMin}</p>}
          </div>
          
            <div>
              <label className="form-label">Maximum Experience (Years)</label>
                  <input
                type="number"
                name="requirements.experience.max"
                value={formData.requirements?.experience?.max || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  requirements: {
                    ...prev.requirements,
                    experience: { ...prev.requirements?.experience, max: e.target.value }
                  }
                }))}
                className="input-field"
                placeholder="10"
                min="0"
              />
                  </div>
              </div>
        </motion.div>

        {/* Compensation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <DollarSign className="w-5 h-5 mr-2" />
            Compensation
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div>
              <label className="form-label">Minimum Salary</label>
                    <input
                      type="number"
                      name="salary.min"
                value={formData.salary?.min || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  salary: { ...prev.salary, min: e.target.value }
                }))}
                className="input-field"
                placeholder="50000"
              />
                </div>
                
                <div>
              <label className="form-label">Maximum Salary</label>
                    <input
                      type="number"
                      name="salary.max"
                value={formData.salary?.max || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  salary: { ...prev.salary, max: e.target.value }
                }))}
                className={`input-field ${errors.salaryMax ? 'border-red-300' : ''}`}
                placeholder="80000"
              />
              {errors.salaryMax && <p className="mt-2 text-sm text-red-600">{errors.salaryMax}</p>}
                </div>
                
                <div>
              <label className="form-label">Salary Period</label>
                  <select
                    name="salary.period"
                value={formData.salary?.period || 'Yearly'}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  salary: { ...prev.salary, period: e.target.value }
                }))}
                className="input-field"
              >
                <option value="Yearly">Yearly</option>
                <option value="Monthly">Monthly</option>
                <option value="Hourly">Hourly</option>
                  </select>
                </div>
              </div>
        </motion.div>

        {/* Job Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Job Description
          </h2>
          
          <div className="space-y-6">
            <div>
              <label className="form-label">Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                rows={8}
                className={`input-field ${errors.description ? 'border-red-300' : ''}`}
                placeholder="Provide a detailed description of the job role, responsibilities, and what makes this opportunity exciting..."
              />
              {errors.description && <p className="mt-2 text-sm text-red-600">{errors.description}</p>}
              </div>
            </div>
        </motion.div>

        {/* Requirements and Responsibilities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <Users className="w-5 h-5 mr-2" />
            Requirements & Responsibilities
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Requirements */}
            <div>
              <label className="form-label">Requirements</label>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={currentRequirement}
                    onChange={(e) => setCurrentRequirement(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addToList('requirements.other', currentRequirement, setCurrentRequirement);
                      }
                    }}
                className="input-field"
                    placeholder="Add a requirement..."
                  />
                  <button
                    type="button"
                    onClick={() => addToList('requirements.other', currentRequirement, setCurrentRequirement)}
                    className="btn-secondary px-3"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {formData.requirements.other.map((req, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                      <span className="text-sm text-gray-700">{req}</span>
                      <button
                        type="button"
                        onClick={() => removeFromList('requirements.other', index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Responsibilities */}
            <div>
              <label className="form-label">Responsibilities</label>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={currentResponsibility}
                    onChange={(e) => setCurrentResponsibility(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addToList('responsibilities', currentResponsibility, setCurrentResponsibility);
                      }
                    }}
                className="input-field"
                    placeholder="Add a responsibility..."
                  />
                  <button
                    type="button"
                    onClick={() => addToList('responsibilities', currentResponsibility, setCurrentResponsibility)}
                    className="btn-secondary px-3"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
            </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {formData.responsibilities.map((resp, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                      <span className="text-sm text-gray-700">{resp}</span>
                      <button
                        type="button"
                        onClick={() => removeFromList('responsibilities', index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
          </div>
        </div>
        
          {/* Skills */}
          <div className="mt-8">
            <label className="form-label">Required Skills</label>
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={currentSkill}
                  onChange={(e) => setCurrentSkill(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (currentSkill.trim()) {
                        const newSkill = {
                          name: currentSkill.trim(),
                          level: 'Intermediate',
                          mandatory: false
                        };
                        setFormData(prev => ({
                          ...prev,
                          requirements: {
                            ...prev.requirements,
                            skills: [...prev.requirements.skills, newSkill]
                          }
                        }));
                        setCurrentSkill('');
                      }
                    }
                  }}
                  className="input-field"
                  placeholder="Add a skill (e.g. React, Python, Project Management)..."
                />
            <button
              type="button"
                  onClick={() => {
                    if (currentSkill.trim()) {
                      const newSkill = {
                        name: currentSkill.trim(),
                        level: 'Intermediate',
                        mandatory: false
                      };
                      setFormData(prev => ({
                        ...prev,
                        requirements: {
                          ...prev.requirements,
                          skills: [...prev.requirements.skills, newSkill]
                        }
                      }));
                      setCurrentSkill('');
                    }
                  }}
                  className="btn-secondary px-3"
                >
                  <Plus className="w-4 h-4" />
            </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.requirements.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                  >
                    {skill.name || skill}
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          requirements: {
                            ...prev.requirements,
                            skills: prev.requirements.skills.filter((_, i) => i !== index)
                          }
                        }));
                      }}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Additional Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <Calendar className="w-5 h-5 mr-2" />
            Additional Information
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="form-label">Application Deadline</label>
              <input
                type="date"
                name="applicationDeadline"
                value={formData.applicationDeadline}
                onChange={handleChange}
                className={`input-field ${errors.applicationDeadline ? 'border-red-300' : ''}`}
              />
              {errors.applicationDeadline && <p className="mt-2 text-sm text-red-600">{errors.applicationDeadline}</p>}
            </div>

            <div>
              <label className="form-label">Urgency</label>
              <select
                name="urgent"
                value={formData.urgent}
                onChange={handleChange}
                className="input-field"
              >
                <option value="false">Normal Priority</option>
                <option value="true">Urgent</option>
              </select>
            </div>

            <div>
              <label className="form-label">Education Level</label>
              <select
                name="requirements.education.degree"
                value={formData.requirements?.education?.degree || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  requirements: {
                    ...prev.requirements,
                    education: { ...prev.requirements?.education, degree: e.target.value }
                  }
                }))}
                className="input-field"
              >
                <option value="">Not specified</option>
                <option value="High School">High School</option>
                <option value="Associate Degree">Associate Degree</option>
                <option value="Bachelor's Degree">Bachelor's Degree</option>
                <option value="Master's Degree">Master's Degree</option>
                <option value="Doctorate">Doctorate</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Submit Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="flex flex-col sm:flex-row gap-4 justify-end"
        >
            <button
              type="button"
              onClick={() => navigate('/jobs')}
              className="btn-secondary"
            >
              Cancel
            </button>
            
          {!isEditing && (
            <button
              type="button"
              onClick={handleSaveAsDraft}
              disabled={submitting}
              className="btn-secondary flex items-center"
            >
              <Save className="w-4 h-4 mr-2" />
              Save as Draft
            </button>
          )}

          <button
            type={isEditing ? "submit" : "button"}
            onClick={isEditing ? undefined : handlePublish}
            disabled={submitting}
            className="btn-primary flex items-center"
          >
            {submitting ? (
              <div className="loading-spinner mr-2"></div>
            ) : (
              <Globe className="w-4 h-4 mr-2" />
            )}
            {isEditing ? 'Update Job' : 'Publish Job'}
          </button>

         
    </motion.div>
      </form>
    </div>
  );
};

export default JobForm; 